import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingBag, Search, Plus, Minus, Settings,
  Calculator, Shirt, FileText, Check, X, Eye, EyeOff,
  DollarSign, Percent, TrendingUp, Printer,
  Copy, ClipboardCheck, Store, User, Phone, Mail, StickyNote, Clock,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import MainNav from "@/components/MainNav";

// Two retail order types (Blank Garment sales handled via Shopify)
const ORDER_TYPE = {
  PRINTED_GARMENT: "printed",      // Standard: Garment + Print pricing
  BYO_GARMENT: "byo",              // BYO: Customer brings garment, print-only pricing
};

// Order type display configuration
const ORDER_TYPE_CONFIG = {
  [ORDER_TYPE.PRINTED_GARMENT]: {
    label: "Printed Garment",
    description: "Garment + prints",
    icon: "Printer",
    color: "primary",
    requiresGarment: true,
    requiresPrints: true,
    requiresCustomerInfo: true,
  },
  [ORDER_TYPE.BYO_GARMENT]: {
    label: "BYO Garment Print",
    description: "Customer's garment",
    icon: "ShoppingBag",
    color: "amber",
    requiresGarment: false,
    requiresPrints: true,
    requiresCustomerInfo: true,
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default pricing (used while loading)
const DEFAULT_PRICING = {
  print_prices: { cap: 20, small: 25, medium: 25, large: 28, extra_large: 32 },
  quantity_discounts: {
    qty_2: { mode: "fixed", value: 2 },
    qty_3: { mode: "fixed", value: 3 },
    qty_4_6: { mode: "fixed", value: 5 },
    qty_7_10: { mode: "fixed", value: 7 },
  },
  additional_print_discount: { mode: "fixed", value: 5 },
  byo_pricing: { one_print: 35, two_prints: 50, three_prints: 65, additional_prints: 15 },
  rounding: { mode: "none" },
};

// Print size labels
const PRINT_SIZE_LABELS = {
  cap: "Cap",
  small: "Small",
  medium: "Medium",
  large: "Large",
  extra_large: "XL",
};

// Estimated print cost per size (for margin calculation)
const ESTIMATED_PRINT_COSTS = {
  cap: 5,
  small: 6,
  medium: 8,
  large: 10,
  extra_large: 14,
};

// Create default print location
const createPrintLocation = (size = "large") => ({
  id: crypto.randomUUID(),
  size: size,
});

export default function RetailCalculator() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  // Pricing settings from API
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  
  // Order type: one of three explicit types
  const [orderType, setOrderType] = useState(ORDER_TYPE.PRINTED_GARMENT);
  
  // Calculator state
  const [garment, setGarment] = useState(null);
  const [garmentSearch, setGarmentSearch] = useState("");
  const [garmentResults, setGarmentResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [printLocations, setPrintLocations] = useState([createPrintLocation("large")]);
  const [quantity, setQuantity] = useState(1);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [showMarginDetails, setShowMarginDetails] = useState(true);
  
  // Shopify integration state
  const [retailOrderNumber, setRetailOrderNumber] = useState(null);
  const [copiedPrintCharge, setCopiedPrintCharge] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  
  // Customer info modal state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [expectedProductionTime, setExpectedProductionTime] = useState(60); // Default 1 hour (60 minutes)
  
  // Derived state - explicit type checks
  const isBYOSale = orderType === ORDER_TYPE.BYO_GARMENT;
  const isPrintedSale = orderType === ORDER_TYPE.PRINTED_GARMENT;
  const orderConfig = ORDER_TYPE_CONFIG[orderType];

  // Load pricing settings from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/retail-pricing`);
        setPricing({
          print_prices: res.data.print_prices || DEFAULT_PRICING.print_prices,
          quantity_discounts: res.data.quantity_discounts || DEFAULT_PRICING.quantity_discounts,
          additional_print_discount: res.data.additional_print_discount || DEFAULT_PRICING.additional_print_discount,
          byo_pricing: res.data.byo_pricing || DEFAULT_PRICING.byo_pricing,
          rounding: res.data.rounding || DEFAULT_PRICING.rounding,
        });
      } catch (error) {
        console.error("Failed to load retail pricing", error);
      } finally {
        setPricingLoaded(true);
      }
    };
    fetchPricing();
  }, []);

  // Generate retail order number on mount
  useEffect(() => {
    const generateOrderNumber = async () => {
      try {
        // Fetch next job number from backend
        const res = await axios.get(`${API}/jobs/next-job-number`);
        setRetailOrderNumber(res.data.job_number || `AA-${Date.now().toString(36).toUpperCase()}`);
      } catch {
        // Fallback to timestamp-based number
        setRetailOrderNumber(`AA-${Date.now().toString(36).toUpperCase()}`);
      }
    };
    generateOrderNumber();
  }, []);

  // Get print price from settings
  const getPrintPrice = (size) => {
    return pricing.print_prices[size] || 25;
  };

  // Get additional print discount
  const getAdditionalPrintDiscount = (basePrice) => {
    const { mode, value } = pricing.additional_print_discount;
    if (mode === "percentage") {
      return basePrice * (value / 100);
    }
    return value; // fixed
  };

  // Get quantity discount from settings
  // Returns the discount item (mode + value) for the quantity, or null if no discount
  const getQuantityDiscountConfig = (qty) => {
    const { qty_2, qty_3, qty_4_6, qty_7_10 } = pricing.quantity_discounts;
    if (qty >= 7 && qty <= 10) return qty_7_10;
    if (qty >= 4 && qty <= 6) return qty_4_6;
    if (qty === 3) return qty_3;
    if (qty === 2) return qty_2;
    return null;
  };

  // Calculate the actual discount amount based on mode and print total
  const calculateQuantityDiscount = (qty, printTotal) => {
    const config = getQuantityDiscountConfig(qty);
    if (!config) return 0;
    
    // Backward compatibility: handle old numeric-only values
    if (typeof config === "number") {
      return config;
    }
    
    const { mode, value } = config;
    if (mode === "percentage") {
      return printTotal * (value / 100);
    }
    return value; // fixed
  };

  // Get BYO price from settings
  const getBYOPrice = (numPrints) => {
    const byoPricing = pricing.byo_pricing || DEFAULT_PRICING.byo_pricing;
    const { one_print = 35, two_prints = 50, three_prints = 65, additional_prints = 15 } = byoPricing;
    if (numPrints === 1) return one_print;
    if (numPrints === 2) return two_prints;
    if (numPrints === 3) return three_prints;
    return three_prints + (numPrints - 3) * additional_prints;
  };

  // Search garments from product library
  const searchGarments = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setGarmentResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { search: query, active_only: true, limit: 8 },
      });
      setGarmentResults(res.data.products || []);
    } catch (error) {
      console.error("Search failed", error);
      setGarmentResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (garmentSearch) {
        searchGarments(garmentSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [garmentSearch, searchGarments]);

  // Select a garment from search results
  const selectGarment = (product) => {
    setGarment(product);
    setGarmentSearch(`${product.brand || ""} ${product.style_code || ""} ${product.product_name || ""}`.trim());
    setGarmentResults([]);
  };

  // Add a new print location
  const addPrintLocation = () => {
    setPrintLocations([...printLocations, createPrintLocation("medium")]);
  };

  // Remove a print location
  const removePrintLocation = (id) => {
    if (printLocations.length > 1) {
      setPrintLocations(printLocations.filter(loc => loc.id !== id));
    }
  };

  // Update a print location's size
  const updatePrintLocationSize = (id, size) => {
    setPrintLocations(printLocations.map(loc => 
      loc.id === id ? { ...loc, size } : loc
    ));
  };

  // Calculate print total per garment (before qty discount)
  const calculatePrintTotalPerGarment = () => {
    let total = 0;
    printLocations.forEach((loc, index) => {
      const basePrice = getPrintPrice(loc.size);
      if (index === 0) {
        total += basePrice;
      } else {
        const discount = getAdditionalPrintDiscount(basePrice);
        total += Math.max(0, basePrice - discount);
      }
    });
    return total;
  };

  // Calculate estimated print cost per garment (for margin)
  const calculatePrintCostPerGarment = () => {
    let total = 0;
    printLocations.forEach((loc) => {
      total += ESTIMATED_PRINT_COSTS[loc.size] || 8;
    });
    return total;
  };

  // Calculate total retail price based on order type
  const calculateTotal = () => {
    // BYO GARMENT PRINT: BYO pricing only (no garment price)
    if (isBYOSale) {
      const numPrints = printLocations.length;
      const pricePerGarment = getBYOPrice(numPrints);
      return pricePerGarment * quantity;
    }
    
    // PRINTED GARMENT SALE: garment + print pricing
    const garmentPrice = garment?.retail_price || 0;
    const printTotalPerGarment = calculatePrintTotalPerGarment();
    const qtyDiscount = calculateQuantityDiscount(quantity, printTotalPerGarment);
    const discountedPrintTotal = Math.max(0, printTotalPerGarment - qtyDiscount);
    
    return (garmentPrice * quantity) + (discountedPrintTotal * quantity);
  };

  // Apply rounding to a total based on settings
  const applyRounding = (total) => {
    const mode = pricing.rounding?.mode || "none";
    if (mode === "none") return total;
    if (mode === "nearest_1") return Math.round(total);
    if (mode === "up_1") return Math.ceil(total);
    if (mode === "nearest_5") return Math.round(total / 5) * 5;
    return total;
  };

  // Get rounded total for display
  const getRoundedTotal = () => applyRounding(calculateTotal());

  // Calculate print-only total (for Shopify integration - excludes garment price)
  const calculatePrintOnlyTotal = () => {
    // BYO GARMENT PRINT: the full BYO price is the print service charge
    if (isBYOSale) {
      const numPrints = printLocations.length;
      const pricePerGarment = getBYOPrice(numPrints);
      return applyRounding(pricePerGarment * quantity);
    }
    
    // PRINTED GARMENT SALE: calculate print portion only
    const printTotalPerGarment = calculatePrintTotalPerGarment();
    const qtyDiscount = calculateQuantityDiscount(quantity, printTotalPerGarment);
    const discountedPrintTotal = Math.max(0, printTotalPerGarment - qtyDiscount);
    return applyRounding(discountedPrintTotal * quantity);
  };

  // Copy print charge to clipboard (formatted for Shopify)
  const copyPrintCharge = async () => {
    const printTotal = calculatePrintOnlyTotal();
    const text = `Custom Print Service – ${retailOrderNumber} – $${printTotal.toFixed(0)}`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPrintCharge(true);
      toast.success("Print charge copied to clipboard");
      setTimeout(() => setCopiedPrintCharge(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Copy amount only to clipboard
  const copyPrintAmount = async () => {
    const printTotal = calculatePrintOnlyTotal();
    const text = printTotal.toFixed(2);
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAmount(true);
      toast.success("Amount copied to clipboard");
      setTimeout(() => setCopiedAmount(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Calculate margin details (admin only)
  const calculateMargin = () => {
    // BYO GARMENT PRINT: margin from print only (no garment cost)
    if (isBYOSale) {
      const numPrints = printLocations.length;
      const sellPrice = getBYOPrice(numPrints) || 0;
      const printCost = calculatePrintCostPerGarment();
      const totalCost = printCost;
      const marginDollars = sellPrice - totalCost;
      const marginPercent = sellPrice > 0 ? (marginDollars / sellPrice) * 100 : 0;
      
      // Calculate rounding impact
      const unroundedTotal = calculateTotal();
      const roundedTotal = applyRounding(unroundedTotal);
      const roundingDiff = roundedTotal - unroundedTotal;
      
      return {
        garmentCost: 0,
        printCost: printCost,
        totalCost: totalCost,
        sellPrice: sellPrice,
        marginDollars: marginDollars,
        marginPercent: marginPercent,
        isBYOSale: true,
        roundingDiff,
        unroundedTotal,
        roundedTotal,
      };
    }
    
    // PRINTED GARMENT SALE: margin from garment + print
    const garmentCost = garment?.default_cost || 0;
    const garmentRetail = garment?.retail_price || 0;
    const printCost = calculatePrintCostPerGarment();
    const printRetail = calculatePrintTotalPerGarment();
    const qtyDiscount = calculateQuantityDiscount(quantity, printRetail);
    const discountedPrintRetail = Math.max(0, printRetail - qtyDiscount);
    
    const totalCost = garmentCost + printCost;
    const sellPrice = garmentRetail + discountedPrintRetail;
    const marginDollars = sellPrice - totalCost;
    const marginPercent = sellPrice > 0 ? (marginDollars / sellPrice) * 100 : 0;
    
    // Calculate rounding impact on total order
    const unroundedTotal = calculateTotal();
    const roundedTotal = applyRounding(unroundedTotal);
    const roundingDiff = roundedTotal - unroundedTotal;
    
    return {
      garmentCost,
      garmentRetail,
      printCost,
      printRetail: discountedPrintRetail,
      totalCost,
      sellPrice,
      marginDollars,
      marginPercent,
      roundingDiff,
      unroundedTotal,
      roundedTotal,
    };
  };

  // Get detailed price breakdown
  const getPriceBreakdown = () => {
    // BYO GARMENT PRINT: BYO pricing only
    if (isBYOSale) {
      const numPrints = printLocations.length;
      const pricePerGarment = getBYOPrice(numPrints);
      return {
        isBYO: true,
        byoPrice: pricePerGarment,
        numPrints,
        quantity,
        total: pricePerGarment * quantity,
      };
    }
    
    // PRINTED GARMENT SALE: garment + print breakdown
    const garmentPrice = garment?.retail_price || 0;
    const garmentTotal = garmentPrice * quantity;
    
    const printBreakdown = printLocations.map((loc, index) => {
      const basePrice = getPrintPrice(loc.size);
      const isDiscounted = index > 0;
      const discount = isDiscounted ? getAdditionalPrintDiscount(basePrice) : 0;
      const price = Math.max(0, basePrice - discount);
      return {
        size: loc.size,
        basePrice,
        isDiscounted,
        discount,
        price,
      };
    });
    
    const printTotalPerGarment = printBreakdown.reduce((sum, p) => sum + p.price, 0);
    const qtyDiscount = calculateQuantityDiscount(quantity, printTotalPerGarment);
    const qtyDiscountConfig = getQuantityDiscountConfig(quantity);
    const discountedPrintPerGarment = Math.max(0, printTotalPerGarment - qtyDiscount);
    const printTotal = discountedPrintPerGarment * quantity;
    
    return {
      isBYO: false,
      garmentPrice,
      garmentTotal,
      printBreakdown,
      printTotalPerGarment,
      qtyDiscount,
      qtyDiscountConfig,
      discountedPrintPerGarment,
      printTotal,
      quantity,
      total: garmentTotal + printTotal,
    };
  };

  // Validate order before creation based on order type
  const validateOrder = () => {
    const errors = [];
    
    // BYO GARMENT PRINT: requires at least one print location
    if (isBYOSale) {
      if (printLocations.length === 0) {
        errors.push("Please add at least one print location");
      }
    }
    // PRINTED GARMENT SALE: requires garment
    else if (isPrintedSale) {
      if (!garment) {
        errors.push("Please select a garment");
      }
    }
    
    return errors;
  };

  // Handle Create Job button click - opens customer info modal
  const handleCreateJobClick = () => {
    // Validate order based on type
    const errors = validateOrder();
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }
    
    // For both printed and BYO orders, customer info is required
    setShowCustomerModal(true);
  };

  // Create job from retail order (called after customer info modal submit)
  const createJob = async () => {
    // Customer info is always required for retail jobs
    if (!customerInfo.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!customerInfo.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    
    setIsCreatingJob(true);
    
    try {
      // BYO GARMENT PRINT: Create production job with BYO garment
      // PRINTED GARMENT SALE: Create production job with selected garment
      
      // Build garment data
      const garmentData = isBYOSale 
        ? {
            id: crypto.randomUUID(),
            name: "BYO Customer Garment",
            colour: "",
            cost: 0,
            quantity: quantity,
          }
        : {
            id: crypto.randomUUID(),
            name: `${garment.brand || ""} ${garment.style_code || ""} ${garment.product_name || ""}`.trim(),
            colour: garment.available_colours?.[0] || "",
            cost: garment.default_cost || 0,
            quantity: quantity,
            product_id: garment.id,
          };

      // Build artwork groups
      const artworkGroups = printLocations.map((loc, index) => {
        const placementNames = {
          cap: "Cap Front",
          small: index === 0 ? "Front Left Chest" : "Right Sleeve",
          medium: index === 0 ? "Front Centre" : "Back Centre",
          large: index === 0 ? "Back Centre" : "Front Centre",
          extra_large: "Full Back",
        };
        
        const sizeTiers = {
          cap: "CAP (Cap Front Print)",
          small: "S (Up to 105cm² - Pocket/Tag)",
          medium: "M (Up to 315cm² - Standard Chest)",
          large: "L (Up to 630cm² - Oversized Chest)",
          extra_large: "XL (Up to 1260cm² - Full Back)",
        };
        
        return {
          id: crypto.randomUUID(),
          print_method: "UltraColour Max",
          placement_name: placementNames[loc.size] || "Custom",
          size_tier: sizeTiers[loc.size] || "M (Up to 315cm² - Standard Chest)",
          allocations: [{ garment_id: garmentData.id, placement_count: 1 }],
          compare_enabled: false,
          blocker: "Standard",
          colours: 1,
          print_colour: "Other",
          light_on_dark: false,
          prints_per_sheet: 1,
          custom_colour_required: false,
          stitch_count: 9999,
          manual_cost: 0,
        };
      });

      const total = getRoundedTotal();
      const printCharge = calculatePrintOnlyTotal();
      const printDesc = printLocations.map(l => PRINT_SIZE_LABELS[l.size]).join(" + ");
      
      // Determine job name and subtype
      const orderSubtype = isBYOSale ? "BYO Garment Print" : "Printed Garment Sale";
      const jobName = isBYOSale
        ? `Retail BYO - ${customerInfo.name} - ${printLocations.length} print${printLocations.length > 1 ? 's' : ''} × ${quantity}`
        : `Retail - ${customerInfo.name} - ${garment?.style_code || 'Custom'} × ${quantity}`;
      
      // Build structured notes - Retail/Design Notes at the top, then order details
      const notesLines = [];
      
      // Add Retail/Design Notes prominently at the top if provided
      if (customerInfo.notes && customerInfo.notes.trim()) {
        notesLines.push("=== RETAIL / DESIGN NOTES ===");
        notesLines.push(customerInfo.notes.trim());
        notesLines.push("");
        notesLines.push("=== ORDER DETAILS ===");
      }
      
      notesLines.push(`Order Type: ${orderSubtype}`);
      notesLines.push(`Total: $${total.toFixed(2)}`);
      notesLines.push(`Print Charge: $${printCharge.toFixed(2)}`);
      notesLines.push(`Prints: ${printDesc}`);
      
      const notesText = notesLines.join("\n");

      // Use the correct endpoint /api/jobs/save
      const jobData = {
        job_name: jobName,
        quote_date: new Date().toISOString().split('T')[0],
        status: "In Production",  // Retail jobs default to "In Production"
        client_type: "Retail",
        margin_percent: 0.45,
        design_required: false,
        design_fee: 0,
        apply_margin_to_design: false,
        shipping: 0,
        misc_costs: 0,
        garments: [garmentData],
        artwork_groups: artworkGroups,
        supplier_shipping: [],
        client: {
          business_name: "",
          contact_name: customerInfo.name,
          email: customerInfo.email || "",
          phone: customerInfo.phone,
          shipping_address: "",
        },
        notes: notesText,
        // Store retail design notes separately for easy access
        retail_design_notes: customerInfo.notes && customerInfo.notes.trim() ? customerInfo.notes.trim() : null,
        // Store retail order total for Production Board display
        retail_total: total,
        // Expected production time in minutes
        expected_production_time: expectedProductionTime,
      };

      const res = await axios.post(`${API}/jobs/save`, jobData);
      const jobId = res.data.id;
      const jobNumber = res.data.job_number;
      
      toast.success(`Retail job ${jobNumber} created successfully!`);
      setShowCustomerModal(false);
      resetCalculator();
      setCustomerInfo({ name: "", phone: "", email: "", notes: "" });
      setExpectedProductionTime(60); // Reset to default
      navigate(`/?job=${jobId}`);
    } catch (error) {
      console.error("Failed to create job", error);
      const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to create job: ${errorMessage}`);
    } finally {
      setIsCreatingJob(false);
    }
  };

  // Reset calculator
  const resetCalculator = () => {
    setGarment(null);
    setGarmentSearch("");
    setPrintLocations([createPrintLocation("large")]);
    setQuantity(1);
    setOrderType(ORDER_TYPE.PRINTED_GARMENT);
  };

  const total = calculateTotal();
  const roundedTotal = getRoundedTotal();
  const hasRounding = pricing.rounding?.mode !== "none" && roundedTotal !== total;
  const breakdown = getPriceBreakdown();
  const margin = isAdmin ? calculateMargin() : null;

  return (
    <div className="min-h-screen bg-aa-bg">
      {/* Global Navigation */}
      <MainNav showQuoteActions={false} />

      {/* Page Controls Row */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-aa-container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-aa-md bg-aa-retail/10">
              <Calculator className="w-5 h-5 text-aa-retail" />
            </div>
            <div>
              <h1 className="text-aa-xl font-bold text-gray-900">Retail Calculator</h1>
              <p className="text-aa-xs text-gray-500">Quick retail pricing</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/settings/retail-pricing">
                <button className="flex items-center gap-2 px-4 py-2.5 rounded-aa-md bg-aa-card shadow-aa-button text-aa-sm font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset">
                  <Settings className="w-4 h-4" />
                  Pricing
                </button>
              </Link>
            )}
            <button 
              onClick={resetCalculator}
              className="flex items-center gap-2 px-4 py-2.5 rounded-aa-md bg-aa-card shadow-aa-button text-aa-sm font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
            >
              Clear
            </button>
            <Link to="/quote">
              <button className="px-4 py-2.5 rounded-aa-md text-aa-sm font-medium text-gray-600 hover:text-aa-primary hover:bg-aa-bg transition-all">
                Full Quote Builder
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-aa-container mx-auto px-6 py-6 pb-32">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calculator Inputs */}
          <div className="space-y-4">
            {/* Order Type Selector */}
            <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
              <h3 className="text-aa-xs font-medium text-gray-500 uppercase tracking-wide mb-4">Order Type</h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Printed Garment Sale */}
                <button
                  onClick={() => setOrderType(ORDER_TYPE.PRINTED_GARMENT)}
                  className={`p-4 rounded-aa-lg border-2 text-center transition-all ${
                    isPrintedSale 
                      ? 'border-aa-primary bg-aa-primary/5 shadow-aa-card' 
                      : 'border-gray-200 bg-aa-bg hover:border-gray-300 shadow-aa-inset'
                  }`}
                  data-testid="order-type-printed"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`w-12 h-12 rounded-aa-md flex items-center justify-center ${
                      isPrintedSale ? 'bg-aa-primary/10' : 'bg-white shadow-sm'
                    }`}>
                      <Printer className={`w-6 h-6 ${isPrintedSale ? 'text-aa-primary' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className={`font-semibold text-aa-sm ${isPrintedSale ? 'text-aa-primary' : 'text-gray-700'}`}>
                        Printed Garment
                      </p>
                      <p className="text-aa-xs text-gray-500">Garment + prints</p>
                    </div>
                  </div>
                </button>
                
                {/* BYO Garment Print */}
                <button
                  onClick={() => setOrderType(ORDER_TYPE.BYO_GARMENT)}
                  className={`p-4 rounded-aa-lg border-2 text-center transition-all ${
                    isBYOSale 
                      ? 'border-aa-warning bg-aa-warning/5 shadow-aa-card' 
                      : 'border-gray-200 bg-aa-bg hover:border-gray-300 shadow-aa-inset'
                  }`}
                  data-testid="order-type-byo"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`w-12 h-12 rounded-aa-md flex items-center justify-center ${
                      isBYOSale ? 'bg-aa-warning/10' : 'bg-white shadow-sm'
                    }`}>
                      <ShoppingBag className={`w-6 h-6 ${isBYOSale ? 'text-aa-warning' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className={`font-semibold text-aa-sm ${isBYOSale ? 'text-aa-warning' : 'text-gray-700'}`}>
                        BYO Garment
                      </p>
                      <p className="text-aa-xs text-gray-500">Customer's garment</p>
                    </div>
                  </div>
                </button>
              </div>
              
              {/* BYO Pricing Info */}
              {isBYOSale && (
                <div className="mt-4 p-3 bg-aa-warning/10 border border-aa-warning/30 rounded-aa-md">
                  <p className="text-aa-xs font-medium text-aa-warning mb-1">BYO Pricing (per garment):</p>
                  <div className="grid grid-cols-4 gap-2 text-aa-xs text-aa-warning/80">
                    <span>1 print: ${pricing.byo_pricing.one_print}</span>
                    <span>2 prints: ${pricing.byo_pricing.two_prints}</span>
                    <span>3 prints: ${pricing.byo_pricing.three_prints}</span>
                    <span>+${pricing.byo_pricing.additional_prints}/extra</span>
                  </div>
                </div>
              )}
            </div>

            {/* Garment Selection (for Printed sales only, not BYO) */}
            {orderConfig.requiresGarment && (
              <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
                <h3 className="text-aa-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Shirt className="w-4 h-4 text-aa-primary" />
                  Garment
                </h3>
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search product library..."
                      value={garmentSearch}
                      onChange={(e) => {
                        setGarmentSearch(e.target.value);
                        if (!e.target.value) setGarment(null);
                      }}
                      className="w-full pl-10 h-11 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                      data-testid="garment-search"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-3">
                        <div className="w-5 h-5 border-2 border-aa-primary/30 border-t-aa-primary rounded-full animate-spin" />
                      </div>
                    )}
                    {garmentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-white rounded-aa-md shadow-aa-card max-h-64 overflow-auto">
                        {garmentResults.map((product) => (
                          <button
                            key={product.id}
                            className="w-full px-4 py-3 text-left hover:bg-aa-bg flex items-center justify-between border-b border-gray-100 last:border-b-0 transition-colors"
                            onClick={() => selectGarment(product)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-aa-bg rounded-aa-sm font-mono text-aa-xs text-gray-600">
                                {product.style_code || "—"}
                              </span>
                              <span className="font-medium text-aa-sm text-gray-900">
                                {product.brand} {product.product_name}
                              </span>
                            </div>
                            <span className="font-mono text-aa-sm text-aa-primary font-semibold">
                              ${(product.retail_price || 0).toFixed(2)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {garment && (
                    <div className="flex items-center justify-between p-3 bg-aa-success/10 rounded-aa-md border border-aa-success/30">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-aa-success" />
                        <span className="text-aa-sm text-aa-success font-medium">
                          {garment.brand} {garment.style_code}
                        </span>
                      </div>
                      <span className="text-aa-sm font-mono text-aa-primary font-semibold">
                        ${(garment.retail_price || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Print Locations (for Printed and BYO sales) */}
            {orderConfig.requiresPrints && (
              <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-aa-sm font-semibold text-gray-900">
                    Print Locations
                    {isBYOSale && <span className="text-aa-warning font-normal ml-2">(BYO Pricing)</span>}
                  </h3>
                  <button
                    onClick={addPrintLocation}
                    data-testid="add-print-btn"
                    className="flex items-center gap-1 px-3 py-2 rounded-aa-md bg-aa-bg shadow-aa-button text-aa-xs font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                  >
                    <Plus className="w-4 h-4" />
                    Add Print
                  </button>
                </div>
                <div className="space-y-3">
                  {/* BYO: Simple print count display */}
                  {isBYOSale ? (
                    <div className="space-y-3">
                      {printLocations.map((loc, index) => (
                        <div 
                          key={loc.id}
                          className="flex items-center justify-between p-3 rounded-aa-md bg-aa-warning/10 border border-aa-warning/30"
                          data-testid={`print-location-${index}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-aa-warning text-white text-aa-xs font-medium rounded-aa-sm">Print {index + 1}</span>
                            <span className="text-aa-sm text-aa-warning">
                              {PRINT_SIZE_LABELS[loc.size] || "Custom"} size
                            </span>
                          </div>
                          {printLocations.length > 1 && (
                            <button
                              className="p-2 text-aa-warning hover:text-aa-warning/80 transition-colors"
                              onClick={() => removePrintLocation(loc.id)}
                              data-testid={`remove-print-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <div className="p-3 bg-white rounded-aa-md shadow-sm">
                        <p className="text-aa-sm font-medium text-aa-warning">
                          BYO Total: ${(getBYOPrice(printLocations.length) || 0).toFixed(2)}/garment
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Standard Printed Garment: Size selection per print */
                    printLocations.map((loc, index) => (
                  <div 
                    key={loc.id} 
                    className={`flex items-center gap-3 p-4 rounded-aa-md ${
                      index === 0 ? 'bg-aa-primary/5 border border-aa-primary/20' : 'bg-aa-bg shadow-aa-inset'
                    }`}
                    data-testid={`print-location-${index}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-1 text-aa-xs font-medium rounded-aa-sm ${
                          index === 0 ? 'bg-aa-primary text-white' : 'bg-gray-200 text-gray-700'
                        }`}>
                          Print {index + 1}
                        </span>
                        {index > 0 && (
                          <span className="px-2 py-1 text-aa-xs font-medium rounded-aa-sm bg-aa-success/10 text-aa-success border border-aa-success/30">
                            {pricing.additional_print_discount.mode === "percentage" 
                              ? `-${pricing.additional_print_discount.value}%`
                              : `-$${pricing.additional_print_discount.value}`
                            }
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {Object.entries(pricing.print_prices).map(([size, price]) => {
                          const discountedPrice = index === 0 
                            ? price 
                            : Math.max(0, price - getAdditionalPrintDiscount(price));
                          return (
                            <button
                              key={size}
                              onClick={() => updatePrintLocationSize(loc.id, size)}
                              className={`p-2 rounded-aa-md text-center transition-all text-aa-xs sm:text-aa-sm ${
                                loc.size === size 
                                  ? 'bg-aa-primary text-white shadow-md' 
                                  : 'bg-white shadow-aa-button hover:-translate-y-0.5 active:shadow-aa-inset'
                              }`}
                              data-testid={`print-${index}-size-${size}`}
                            >
                              <div className="font-medium">{PRINT_SIZE_LABELS[size]}</div>
                              <div className={`text-[10px] ${loc.size === size ? 'opacity-80' : 'text-gray-500'}`}>
                                ${discountedPrice.toFixed(0)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {printLocations.length > 1 && (
                      <button
                        className="shrink-0 p-2 text-gray-400 hover:text-aa-error transition-colors"
                        onClick={() => removePrintLocation(loc.id)}
                        data-testid={`remove-print-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
                  )}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
              <h3 className="text-aa-sm font-semibold text-gray-900 mb-4">Quantity</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="qty-minus"
                  className="h-14 w-14 flex items-center justify-center rounded-aa-md bg-aa-bg shadow-aa-button text-xl font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                >
                  <Minus className="w-6 h-6" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-14 w-24 text-2xl font-bold text-center bg-aa-bg rounded-aa-md shadow-aa-input focus:outline-none focus:ring-2 focus:ring-aa-primary"
                  data-testid="qty-input"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="qty-plus"
                  className="h-14 w-14 flex items-center justify-center rounded-aa-md bg-aa-bg shadow-aa-button text-xl font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                >
                  <Plus className="w-6 h-6" />
                </button>
                {isPrintedSale && getQuantityDiscountConfig(quantity) && (
                  <span className="px-3 py-2 bg-aa-success/10 text-aa-success text-aa-sm font-medium rounded-aa-md border border-aa-success/30">
                    {(() => {
                      const config = getQuantityDiscountConfig(quantity);
                      if (!config) return null;
                      if (typeof config === "number") return `-$${config}/garment`;
                      return config.mode === "percentage" 
                        ? `-${config.value}%` 
                        : `-$${config.value}/garment`;
                    })()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Price Display */}
          <div className="space-y-4">
            {/* Total Card */}
            <div className="bg-aa-primary rounded-aa-lg p-8 shadow-aa-card text-white">
              <div className="text-center">
                <p className="text-aa-xs font-medium opacity-80 mb-2 uppercase tracking-wide">TOTAL PRICE</p>
                <p className="text-5xl sm:text-6xl font-bold tracking-tight" data-testid="total-price">
                  ${(roundedTotal || 0).toFixed(2)}
                </p>
                {quantity > 1 && (
                  <p className="text-aa-lg opacity-80 mt-2">
                    ${((roundedTotal || 0) / quantity).toFixed(2)} each
                  </p>
                )}
                {/* Admin sees unrounded calculation if rounding applied */}
                {isAdmin && hasRounding && (
                  <p className="text-aa-xs opacity-60 mt-2">
                    Unrounded: ${(total || 0).toFixed(2)} ({pricing.rounding?.mode?.replace(/_/g, " ") || "none"})
                  </p>
                )}
              </div>
            </div>

            {/* Admin Margin Calculator */}
            {isAdmin && margin && (
              <div className="bg-purple-50 rounded-aa-lg p-6 shadow-aa-card border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-aa-sm text-purple-700 font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Margin Analysis (Admin)
                  </h3>
                  <button
                    onClick={() => setShowMarginDetails(!showMarginDetails)}
                    className="p-1.5 rounded-aa-sm hover:bg-purple-100 transition-colors text-purple-600"
                  >
                    {showMarginDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {showMarginDetails && (
                  <div className="space-y-2 text-aa-sm">
                    {isPrintedSale && garment && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Garment cost</span>
                        <span className="font-mono text-aa-error">${(margin.garmentCost || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Est. print cost</span>
                      <span className="font-mono text-aa-error">${(margin.printCost || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t border-purple-200 pt-2">
                      <span className="font-medium">Total cost</span>
                      <span className="font-mono font-semibold text-aa-error">${(margin.totalCost || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sell price</span>
                      <span className="font-mono text-aa-success">${(margin.sellPrice || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between bg-purple-100 -mx-4 px-4 py-3 rounded-aa-md mt-2">
                      <span className="font-semibold text-purple-800">Margin</span>
                      <div className="text-right">
                        <span className="font-mono font-bold text-purple-800">
                          ${(margin.marginDollars || 0).toFixed(2)}
                        </span>
                        <span className="text-purple-600 ml-2 text-aa-xs">
                          ({(margin.marginPercent || 0).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    {/* Rounding Impact (if any) */}
                    {pricing.rounding?.mode !== "none" && margin.roundingDiff !== 0 && (
                      <div className="flex justify-between items-center text-aa-xs pt-2 border-t border-dashed border-purple-200">
                        <span className="text-gray-500">
                          Rounding ({pricing.rounding.mode.replace("_", " ")})
                        </span>
                        <span className={`font-mono ${margin.roundingDiff > 0 ? 'text-aa-success' : 'text-aa-error'}`}>
                          {margin.roundingDiff > 0 ? '+' : ''}{(margin.roundingDiff || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Shopify Print Charge (always shown since both order types have prints) */}
            {calculatePrintOnlyTotal() > 0 && (
              <div className="bg-aa-retail-bg rounded-aa-lg p-6 shadow-aa-card border-l-4 border-aa-retail">
                <h3 className="text-aa-sm text-aa-retail font-semibold flex items-center gap-2 mb-4">
                  <Store className="w-4 h-4" />
                  Shopify Print Charge
                </h3>
                <div className="space-y-3">
                  {/* Print Service Summary */}
                  <div className="bg-white rounded-aa-md p-4 text-center shadow-sm">
                    <p className="text-aa-xs text-aa-retail font-medium mb-1 uppercase tracking-wide">PRINT SERVICE TOTAL</p>
                    <p className="text-3xl font-bold text-aa-retail" data-testid="print-charge-total">
                      ${calculatePrintOnlyTotal().toFixed(2)}
                    </p>
                    <p className="text-aa-xs text-gray-500 mt-1">
                      {retailOrderNumber || 'Generating...'}
                    </p>
                  </div>

                  {/* Copy Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={copyPrintCharge}
                      data-testid="copy-print-charge-btn"
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-aa-md bg-white shadow-aa-button text-aa-xs font-medium text-aa-retail transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                    >
                      {copiedPrintCharge ? (
                        <>
                          <ClipboardCheck className="w-4 h-4 text-aa-success" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy Print Charge
                        </>
                      )}
                    </button>
                    <button
                      onClick={copyPrintAmount}
                      data-testid="copy-print-amount-btn"
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-aa-md bg-white shadow-aa-button text-aa-xs font-medium text-aa-retail transition-all hover:-translate-y-0.5 active:shadow-aa-inset"
                    >
                      {copiedAmount ? (
                        <>
                          <ClipboardCheck className="w-4 h-4 text-aa-success" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4" />
                          Copy Amount
                        </>
                      )}
                    </button>
                  </div>

                  {/* Example Text Preview */}
                  <div className="text-aa-xs text-gray-500 bg-aa-bg rounded-aa-md p-2 font-mono">
                    <p className="truncate">Custom Print Service – {retailOrderNumber} – ${calculatePrintOnlyTotal().toFixed(0)}</p>
                  </div>

                  {/* Staff Workflow Hint */}
                  <p className="text-aa-xs text-gray-500">
                    Use "Copy Print Charge" for Shopify. Garments are added separately by barcode/variant.
                  </p>
                </div>
              </div>
            )}

            {/* Detailed Breakdown */}
            <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
              <h3 className="text-aa-xs font-medium text-gray-500 uppercase tracking-wide mb-4">Price Breakdown</h3>
              <div>
                {breakdown.isBYO ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-aa-warning/10 text-aa-warning text-aa-xs font-medium rounded-aa-sm border border-aa-warning/30">
                          BYO
                        </span>
                        <span className="text-aa-sm">{breakdown.numPrints || 0} print{(breakdown.numPrints || 0) > 1 ? 's' : ''}</span>
                      </div>
                      <span className="font-mono font-semibold text-aa-sm">${(breakdown.byoPrice || 0).toFixed(2)}/ea</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-aa-sm">× {breakdown.quantity || 1} garment{(breakdown.quantity || 1) > 1 ? 's' : ''}</span>
                      <span className="font-mono font-semibold text-aa-sm">${(breakdown.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-aa-md font-semibold">Total</span>
                      <span className="font-mono font-bold text-aa-lg">${(breakdown.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {garment && (
                      <div className="pb-3 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-aa-sm font-medium">Garment</span>
                          <span className="text-aa-xs text-gray-500">${(breakdown.garmentPrice || 0).toFixed(2)} × {breakdown.quantity || 1}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-aa-xs text-gray-500">{garment.style_code}</span>
                          <span className="font-mono font-semibold text-aa-sm">${(breakdown.garmentTotal || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="pb-3 border-b border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-aa-sm font-medium">Print Locations</span>
                        <span className="text-aa-xs text-gray-500">${(breakdown.printTotalPerGarment || 0).toFixed(2)}/garment</span>
                      </div>
                      {(breakdown.printBreakdown || []).map((print, i) => (
                        <div key={`print-${print.size}-${i}`} className="flex justify-between items-center text-aa-xs py-1">
                          <span className="text-gray-500">
                            {PRINT_SIZE_LABELS[print.size]} print
                            {print.isDiscounted && (
                              <span className="text-aa-success ml-1">
                                ({pricing.additional_print_discount?.mode === "percentage" 
                                  ? `-${pricing.additional_print_discount.value}%`
                                  : `-$${(print.discount || 0).toFixed(0)}`})
                              </span>
                            )}
                          </span>
                          <span className="font-mono">${(print.price || 0).toFixed(2)}</span>
                        </div>
                      ))}
                      
                      {(breakdown.qtyDiscount || 0) > 0 && (
                        <div className="flex justify-between items-center text-aa-xs py-1 mt-2 pt-2 border-t border-dashed border-gray-200">
                          <span className="text-aa-success">
                            Qty discount ({breakdown.quantity || 1} units)
                            {breakdown.qtyDiscountConfig && (
                              <span className="ml-1 opacity-75">
                                ({breakdown.qtyDiscountConfig.mode === "percentage" 
                                  ? `${breakdown.qtyDiscountConfig.value}%` 
                                  : `$${breakdown.qtyDiscountConfig.value}`})
                              </span>
                            )}
                          </span>
                          <span className="font-mono text-aa-success">-${(breakdown.qtyDiscount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                        <span className="text-aa-sm">Print total × {breakdown.quantity || 1}</span>
                        <span className="font-mono font-semibold text-aa-sm">${(breakdown.printTotal || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-aa-md font-semibold">Total</span>
                      <span className="font-mono font-bold text-aa-lg">${(breakdown.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Create Job Button */}
            <button
              onClick={handleCreateJobClick}
              disabled={isCreatingJob || (orderConfig.requiresGarment && !garment)}
              data-testid="create-job-btn"
              className={`w-full h-16 flex items-center justify-center gap-2 text-aa-lg font-semibold text-white rounded-aa-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                isBYOSale 
                  ? 'bg-aa-warning hover:shadow-[0_6px_20px_rgba(245,158,11,0.4)] hover:-translate-y-0.5' 
                  : 'bg-aa-primary hover:shadow-[0_6px_20px_rgba(30,58,95,0.4)] hover:-translate-y-0.5'
              }`}
            >
              {isCreatingJob ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : isBYOSale ? (
                <>
                  <ShoppingBag className="w-5 h-5" />
                  Create BYO Print Job
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Create Retail Job
                </>
              )}
            </button>
            
            {orderConfig.requiresGarment && !garment && (
              <p className="text-center text-aa-sm text-gray-500">
                Select a garment to create a job
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Customer Info Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="sm:max-w-md bg-aa-card rounded-aa-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-aa-lg font-semibold">
              <User className="w-5 h-5 text-aa-primary" />
              {isBYOSale ? 'Create BYO Print Job' : 'Create Retail Job'}
            </DialogTitle>
            <DialogDescription className="text-aa-sm text-gray-500">
              Enter customer information to create this retail job.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Name */}
            <div className="space-y-2">
              <label htmlFor="customer-name" className="flex items-center gap-1 text-aa-sm font-medium text-gray-700">
                <User className="w-4 h-4" />
                Customer Name <span className="text-aa-error">*</span>
              </label>
              <input
                id="customer-name"
                type="text"
                placeholder="Enter customer name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                className="w-full h-10 px-3 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                data-testid="customer-name-input"
              />
            </div>
            
            {/* Phone Number */}
            <div className="space-y-2">
              <label htmlFor="customer-phone" className="flex items-center gap-1 text-aa-sm font-medium text-gray-700">
                <Phone className="w-4 h-4" />
                Phone Number <span className="text-aa-error">*</span>
              </label>
              <input
                id="customer-phone"
                type="tel"
                placeholder="Enter phone number"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                className="w-full h-10 px-3 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                data-testid="customer-phone-input"
              />
            </div>
            
            {/* Email - Optional */}
            <div className="space-y-2">
              <label htmlFor="customer-email" className="flex items-center gap-1 text-aa-sm font-medium text-gray-700">
                <Mail className="w-4 h-4" />
                Email <span className="text-gray-400 text-aa-xs">(optional)</span>
              </label>
              <input
                id="customer-email"
                type="email"
                placeholder="Enter email address"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                className="w-full h-10 px-3 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                data-testid="customer-email-input"
              />
            </div>
            
            {/* Retail / Design Notes - Optional */}
            <div className="space-y-2">
              <label htmlFor="customer-notes" className="flex items-center gap-1 text-aa-sm font-medium text-gray-700">
                <StickyNote className="w-4 h-4" />
                Retail / Design Notes <span className="text-gray-400 text-aa-xs">(optional)</span>
              </label>
              <textarea
                id="customer-notes"
                placeholder="Design book selection, text changes, icon changes, production notes..."
                value={customerInfo.notes}
                onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                className="w-full h-24 px-3 py-2 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm resize-none focus:outline-none focus:ring-2 focus:ring-aa-primary"
                data-testid="customer-notes-input"
              />
              <p className="text-aa-xs text-gray-400">
                Example: "Book design: Mount Bogong Summit" or "Change text to: Smith Family"
              </p>
            </div>

            {/* Expected Production Time */}
            <div className="space-y-2">
              <label className="flex items-center gap-1 text-aa-sm font-medium text-gray-700">
                <Clock className="w-4 h-4" />
                Expected Production Time
              </label>
              <Select 
                value={String(expectedProductionTime)} 
                onValueChange={(v) => setExpectedProductionTime(Number(v))}
              >
                <SelectTrigger data-testid="production-time-select" className="bg-aa-bg shadow-aa-input border-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="1440">24 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-aa-xs text-gray-400">
                Estimated time until order is ready for pickup
              </p>
            </div>

            {/* Order Summary */}
            <div className="bg-aa-bg rounded-aa-md p-4 shadow-aa-inset">
              <p className="text-aa-xs font-medium text-gray-500 uppercase tracking-wide mb-2">ORDER SUMMARY</p>
              <div className="text-aa-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Job Number:</span>
                  <span className="font-mono font-medium text-gray-900">{retailOrderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Print Charge:</span>
                  <span className="font-mono font-medium text-gray-900">${calculatePrintOnlyTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t border-gray-200">
                  <span className="text-gray-900">Total:</span>
                  <span className="font-mono text-aa-primary">${getRoundedTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <button 
              onClick={() => setShowCustomerModal(false)}
              disabled={isCreatingJob}
              className="px-4 py-2.5 rounded-aa-md bg-aa-card shadow-aa-button text-aa-sm font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              onClick={createJob}
              disabled={isCreatingJob || !customerInfo.name.trim() || !customerInfo.phone.trim()}
              data-testid="confirm-create-job-btn"
              className={`px-4 py-2.5 rounded-aa-md text-aa-sm font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                isBYOSale ? 'bg-aa-warning hover:shadow-[0_4px_12px_rgba(245,158,11,0.3)]' : 'bg-aa-primary hover:shadow-[0_4px_12px_rgba(30,58,95,0.3)]'
              }`}
            >
              {isCreatingJob ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2 inline-block" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2 inline-block" />
                  Create Job
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
